import React, { createContext, FunctionComponent, useCallback, useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { configState, languageState } from '../../recoil/atoms';

export interface LanguageContextType {
  language: string;
  setLanguage: (lang: string) => void;
  translate: (key: string, params?: Record<string, string | number | boolean>) => string;
  supportedLanguages: string[];
}

export const LanguageContext = createContext<LanguageContextType>({
  language: 'English',
  setLanguage: () => {},
  translate: key => key,
  supportedLanguages: ['English'],
});

export const LanguageProvider: FunctionComponent<{ children?: React.ReactNode }> = ({ children }) => {
  const { languages, translations } = useRecoilValue(configState);
  const [language, setLanguage] = useRecoilState<string | null>(languageState);

  useEffect(() => {
    if (!language && languages?.default) {
      setLanguage(languages.default);
    }
  }, [language, languages?.default, setLanguage]);

  const effectiveLanguage = language ?? languages?.default ?? 'English';

  const translate = useCallback(
    (key: string, params?: Record<string, string | number | boolean>): string => {
      if ((effectiveLanguage === 'English' && !params) || !translations) return key;

      const translation = translations[key]?.[effectiveLanguage] ?? key;

      if (params) {
        return Object.keys(params).reduce(
          (result, param) =>
            result.replace(new RegExp(`\\$__${param}__\\$`, 'g'), translate((params[param] ?? '').toLocaleString())),
          translation
        );
      }

      return translation;
    },
    [effectiveLanguage, translations]
  );

  return (
    <LanguageContext.Provider
      value={{
        language: effectiveLanguage,
        setLanguage,
        translate,
        supportedLanguages: languages?.supportedLanguages ?? ['English'],
      }}>
      {children}
    </LanguageContext.Provider>
  );
};
